import React, { createContext, useContext, useMemo, useState } from "react";
import { fetchCategoryScore } from "../api/categoryService";
import { createOrFetchAnon } from "../api/anonService";
import { getAnon, saveAnon } from "../utils/localStorage";
import { WSE } from "../utils/websocketEvents";
import { useWebSocketCtx } from "./WebSocketContext";

const GameCtx = createContext(null);

export function useGameCtx() {
  return useContext(GameCtx);
}

export function GameProvider({ children }) {
  const { connect, send, disconnect } = useWebSocketCtx();

  const [gameState, setGameState] = useState("idle");
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [player, setPlayer] = useState(null);
  const [scores, setScores] = useState({});
  const [currentGame, setCurrentGame] = useState(null);

  const value = useMemo(
    () => ({
      gameState,
      setGameState,
      selectedCategory,
      setSelectedCategory,
      player,
      setPlayer,
      scores,
      currentGame,
    }),
    [gameState, selectedCategory, player, scores, currentGame]
  );

  async function ensureAnon(pseudo) {
    const anon = getAnon();

    if (anon && (!pseudo || pseudo === anon.pseudo)) {
      setPlayer({ id: anon.uuid, pseudo: anon.pseudo, isAnon: true });
      return anon;
    }

    const created = await createOrFetchAnon({
      pseudo: pseudo || anon?.pseudo || undefined,
      uuid: anon?.uuid,
    });

    saveAnon(created);
    setPlayer({ id: created.uuid, pseudo: created.pseudo, isAnon: true });
    return created;
  }

  async function loadCategoryScore(slug, userId) {
    const data = await fetchCategoryScore({ slug, userId });
    setScores((prev) => ({ ...prev, [slug]: data }));
    return data;
  }

  function joinQueue() {
    if (!selectedCategory) return;

    setGameState("matchmaking");
    connect(selectedCategory.slug, {
      onOpen: () => {
        send({
          type: WSE.JOIN_QUEUE,
          category: selectedCategory.slug,
          player: player?.id || null,
          pseudo: player?.pseudo || null,
        });
      },
      onMessage: (msg) => {
        if (msg.type === WSE.START_GAME) {
          setCurrentGame({ id: msg.game_id, category: msg.category });
          setGameState("playing");
        }
      },
      onClose: () => {},
    });
  }

  return (
    <GameCtx.Provider
      value={{
        ...value,
        ensureAnon,
        loadCategoryScore,
        joinQueue,
        sendWs: send,
        disconnectWs: disconnect,
      }}
    >
      {children}
    </GameCtx.Provider>
  );
}
